import {
  Avatar,
  Box,
  Button,
  Dialog,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@material-ui/icons'
import firebase from 'firebase/compat/app'
import 'firebase/compat/firestore'
import React, { FC, useEffect, useState } from 'react'
// @ts-ignore
import AsyncSelect from 'react-select/lib/Async'
// @ts-ignore
import { components } from 'react-select'
import type { BusinessType } from '../../../../src/types/business'
import type { Timestamp } from '../../../../src/types/firebase'
import type { JobType } from '../../../../src/types/jobs'
import ButtonWithDisabledTooltip from '../ButtonWithDisabledTooltip'
import Modal from '../mui/Modal'
import ModalHeader from '../mui/ModalHeader'
import AddStaffer from './AddStaffer'
import type { OptionType } from './CreateEditJob'
import styles from './modals.module.css'
import { firestoreHttpsCallable } from '../../staffers/api/firestore/https/util'
import { BeatLoader } from 'react-spinners'

type Props = {
  business: BusinessType
  businessId: string
  createdJob: {
    businessId: string
    description: string
    isFreelanceJob: boolean
    location: firebase.firestore.GeoPoint | null
    timeEnd: Timestamp
    timeStart: Timestamp
    employmentReason: string
    presentStaffers: Array<string>
    salaryCurrency: string
    salaryHourly: string | number
    jobType: string
    options: Array<string>
  }
  disabled: boolean
  disabledReason: string
  goBack: () => void
  invites: Array<OptionType>
  invalidFormReasons: { [reason: string]: string }
  job?: JobType
  jobType: string
  limitedToInvitedUntil: boolean | Timestamp
  onClose: () => void
  onPressSaveJob: () => void
  removeOption: (id: string) => void
  selectOption: (option: OptionType) => void
  togglelimitedToInvitedUntil: () => void
  recommendedWageDialog: 'open' | 'closed' | 'dismissed'
  setDialogClosed: () => void
  setDialogDismissed: () => void
}
// This needs to be one so there isn't skip between 3 results (initial) to 0 after typing first
// letters
const SEARCH_QUERY_MIN_LENGTH = 1

const JobInvitesExternalModal: FC<Props> = ({
  business,
  businessId,
  createdJob,
  disabled,
  disabledReason,
  goBack,
  invites,
  invalidFormReasons,
  job,
  jobType,
  limitedToInvitedUntil,
  onClose,
  onPressSaveJob,
  removeOption,
  selectOption,
  togglelimitedToInvitedUntil,
  recommendedWageDialog,
  setDialogClosed,
  setDialogDismissed,
}) => {
  const [activeStafferId, setActiveStafferId] = useState<string | null>(null)
  const [acceptedWorkAgreements, setAcceptedWorkAgreements] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [data, setData] = useState<OptionType[]>([])

  useEffect(() => {
    setIsProcessing(true)
    try {
      firestoreHttpsCallable('getOfferJobStaffers', {
        businessId,
        groupId: business.groupId,
        jobType,
      }).then((response) => {
        setData(response.data.staffers)
      }).finally(() => {
        setIsProcessing(false)
      })
    } catch (error) {
      console.error((error as Error)?.message ?? 'Failed to get staffers')
      setIsProcessing(false)
    }
  }, [business.groupId, businessId, jobType])

  const openContractPreview = (stafferId: string) =>
    setActiveStafferId(stafferId)

  const hideContractModal = () =>
    setActiveStafferId(null)

  const toggleacceptedWorkAgreements = () =>
    setAcceptedWorkAgreements(!acceptedWorkAgreements)

    const { presentStaffers } = createdJob

    const isSearchable = (searchQuery: string): boolean => searchQuery.length >= SEARCH_QUERY_MIN_LENGTH

    const renderOption = (option: { data: OptionType }) => {
      const { id, label, phone } = option.data
      const { Option } = components
      // This needs to be here because getDefaultOptions is cached and it displays already selected
      const isInvitedAlready = invites.find(({ id: eId }) => eId === id)
      return isInvitedAlready ? null :
        <Option {...option}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ fontWeight: 'bold' }}>{label}</div>
            <div style={{ fontSize: '12px', color: 'gray' }}>{phone}</div>
          </div>
        </Option>
    }

    const getSearchResults = async (searchQueries: string): Promise<OptionType[] | void> => {
      try {
        if (!searchQueries) {
          const results: Array<OptionType> = data
            .filter(({ id }) => {
              const idStr = String(id);
              return !invites.find(({ id: eId }) => String(eId) === idStr) && !presentStaffers.map(String).includes(idStr);
            })
            .sort((option1, option2) => option1.label.localeCompare(option2.label));
          return results;
        }
        const filteredData = data.filter(({ label, phone }) => label.toLowerCase().includes(searchQueries.toLowerCase()) || phone?.toLowerCase().includes(searchQueries.toLowerCase()))
        return filteredData
      } catch (error) {
        console.error((error as Error)?.message ?? 'Failed to get search results')
        return undefined
      }
    }

    return (
      <React.Fragment>
        <ModalHeader close={onClose} goBack={goBack}>
          Invite previous staffers
        </ModalHeader>
        <div style={{ overflowY: 'auto', height: '35rem', width: '55rem', display: 'flex', flexDirection: 'column'}}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 15 }}>
            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 10}}>
              {isProcessing ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                  <BeatLoader color="gray" />
                </div>
              ) : (
                <>
                  {data.length > 0 ? (
                    <>
                      <h2 className={styles.subtitle}>Assign Staffers</h2>
                      <AsyncSelect
                        disabled={!jobType}
                        components={{
                          Option: renderOption,
                        }}
                        placeholder={jobType ? 'Search for previous staffers...' : 'Please select a job type first'}
                        noOptionsMessage={({ inputValue }: { inputValue: string }) =>
                          isSearchable(inputValue)
                            ? 'No results found. A staffer need to have worked at least one shift at your business before inviting them directly'
                            : 'Please enter a valid name of staffer.'
                        }
                        styles={{
                          option: (provided: Record<string, string>) => ({
                            ...provided,
                            color: 'black',
                            display: 'flex',
                            'flex-direction': 'row',
                            'align-items': 'center',
                            width: '100%',
                          }),
                        }}
                        value={null}
                        loadOptions={getSearchResults}
                        onChange={selectOption}
                        isSearchable
                        defaultOptions={data}
                      />
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                      <Typography>No previous staffers found</Typography>
                    </div>
                  )}
                </>
              )}
              {invites && invites.length > 0 && (
                <React.Fragment>
                  <List>
                    <Grid container spacing={3} style={{ maxHeight: '20rem', overflowY: 'auto' }}>
                      {invites &&
                        invites.map(({ id, photoUrl, label }) => (
                          <Grid item xs={6} key={id}>
                            <ListItem key={id}>
                              <ListItemAvatar>
                                <Avatar alt={label} src={photoUrl} />
                              </ListItemAvatar>
                              <ListItemText primary={label} />
                              <Tooltip title="Open contract preview for this employee">
                                <IconButton
                                  edge="end"
                                  aria-label="Open contract preview"
                                  onClick={() => openContractPreview(id)}
                                >
                                  <AssignmentIcon />
                                </IconButton>
                              </Tooltip>
                              {job && job.shifts.every((shift) => shift.staffersConfirmed.includes(id)) ? (
                                <Tooltip title="This staffer has already accepted all shifts of this job">
                                  <div>
                                    <VerifiedUserIcon color="primary" />
                                  </div>
                                </Tooltip>
                              ) : (
                                <ListItemSecondaryAction>
                                  <IconButton edge="end" aria-label="remove" onClick={() => removeOption(id)}>
                                    <CancelIcon color="error" />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              )}
                            </ListItem>
                          </Grid>
                        ))}
                    </Grid>
                  </List>
                </React.Fragment>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
              <div style={{ display: 'flex', flexDirection: 'column'}}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={typeof limitedToInvitedUntil !== 'boolean' && !!limitedToInvitedUntil}
                      checked={!!limitedToInvitedUntil}
                      onChange={togglelimitedToInvitedUntil}
                      color="primary"
                    />
                  }
                  label="Make this job non-exclusive after 6 hours"
                />
                <FormControlLabel
                  control={
                    <Switch
                      disabled={!(invites && invites.length)}
                      checked={!!acceptedWorkAgreements}
                      onChange={toggleacceptedWorkAgreements}
                      color="primary"
                    />
                  }
                  label="I accept the work agreement(s)*"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 10}}>
                  <Button id="go-back-btn" onClick={goBack} variant="contained">
                    Go back
                  </Button>
                  <ButtonWithDisabledTooltip
                    onClick={onPressSaveJob}
                    variant="contained"
                    color="primary"
                    reasons={{
                      ...invalidFormReasons,
                      ...(acceptedWorkAgreements
                        ? {}
                        : { disagreed: 'You need to accept the work agreement(s) before offering the job' }),
                    }}
                    reason={acceptedWorkAgreements ? disabledReason : 'disagreed'}
                    disabled={disabled || !(invites && invites.length) || !acceptedWorkAgreements}
                  >
                    {job && job.id ? 'Save job with invites' : 'Post job with invites'}
                  </ButtonWithDisabledTooltip>
              </div>
            </div>
          </div>
        </div>
        <Modal
          isOpen={!!activeStafferId}
          onRequestClose={hideContractModal}
          contentLabel="AddStaffer"
          ariaHideApp={false}
        >
          <AddStaffer
            business={business}
            isContractPreview
            job={job || createdJob}
            onAcceptStaffer={hideContractModal}
            onClose={hideContractModal}
            stafferId={activeStafferId}
          />
        </Modal>
        <Dialog open={recommendedWageDialog === 'open'} onClose={setDialogClosed}>
          <ModalHeader close={setDialogClosed}>Recommended wage</ModalHeader>
          <Box p={2}>
            <Typography>Hey there! 👋</Typography>
            <br />
            <Typography>
              The wage you&apos;ve set for this same-day job posting is below the recommended 220 NOK per hour. 📉 To
              boost your chances of success, consider offering a higher wage. 🚀
            </Typography>{' '}
            <br />
            <Typography>What would you like to do?</Typography>
            <Box py={1} display="flex" alignItems="center">
              <Button variant="contained" color="primary" onClick={setDialogClosed}>
                Go back
              </Button>
              <Box ml={1}>
                <Button onClick={setDialogDismissed} variant="contained">
                  Post anyway
                </Button>
              </Box>
            </Box>
          </Box>
        </Dialog>
      </React.Fragment>
    )
}

export default JobInvitesExternalModal
